import type { ComponentChildren } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import type { LocalizedText } from '#contract'
import { useT } from '../i18n/index.ts'
import styles from './status-bar.module.css'

/** One item in a menu bar menu. Without `onSelect` it shows disabled. */
export interface OsMenuItem {
  readonly label: LocalizedText
  readonly onSelect?: () => void
}

interface MenuBarMenuProps {
  readonly id: string
  /** What the menu bar shows: a name, or the logo. */
  readonly children: ComponentChildren
  /** The accessible name when `children` isn't text. */
  readonly name?: string
  readonly items: readonly OsMenuItem[]
  /** The menu bar's open menu: one at a time. */
  readonly open: string | null
  readonly onOpen: (id: string | null) => void
}

/** A menu bar title and its dropdown. Once one is open, hovering another opens that one. */
export function MenuBarMenu({ id, children, name, items, open, onOpen }: MenuBarMenuProps) {
  const t = useT()
  const root = useRef<HTMLDivElement>(null)
  const isOpen = open === id

  // A press anywhere else closes it.
  useEffect(() => {
    if (!isOpen) return
    const controller = new AbortController()
    window.addEventListener(
      'pointerdown',
      (event) => {
        if (!root.current?.contains(event.target as Node)) onOpen(null)
      },
      { capture: true, signal: controller.signal },
    )
    return () => {
      controller.abort()
    }
  }, [isOpen, onOpen])

  return (
    <div ref={root} class={styles.menu}>
      <button
        type="button"
        class={styles.menuTitle}
        aria-label={name}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-open={isOpen || undefined}
        onClick={() => {
          onOpen(isOpen ? null : id)
        }}
        onPointerEnter={() => {
          if (open !== null && !isOpen) onOpen(id)
        }}
      >
        {children}
      </button>
      {isOpen && (
        <div class={styles.dropdown} role="menu">
          {items.map((item) => {
            const label = t(item.label)
            return (
              <button
                key={label}
                type="button"
                role="menuitem"
                class={styles.item}
                disabled={!item.onSelect}
                onClick={() => {
                  onOpen(null)
                  item.onSelect?.()
                }}
              >
                {label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
